import React from "react";
import { Link } from "react-router-dom";
import { FiCheck, FiX, FiCalendar } from "react-icons/fi";

const NEU_INSET_SM =
  "shadow-[inset_2px_2px_4px_var(--shadow-dark),inset_-2px_-2px_4px_var(--shadow-light)]";
const PAGE_RAISED_SM =
  "shadow-[-4px_-4px_9px_var(--outer-shadow-light),4px_4px_9px_var(--outer-shadow-dark)]";

/**
 * Weekly plan navigation — used both as a desktop sticky sidebar and inside a
 * mobile drawer. Purely presentational; the parent fetches the week + progress
 * and hands us the compact shape.
 */
const ReadingPlanWeek = ({
  planSlug,
  planName,
  weekStart,
  weekEnd,
  daysByDate = new Map(),
  completedIds = new Set(),
  todayISO,
  selectedISO,
  loading = false,
  error = null,
  onSelect,
  onClose,
  showCloseButton = false,
}) => {
  // 7 slots (Sun→Sat) whether or not the API returned rows for each.
  const slots = [];
  if (weekStart) {
    for (let i = 0; i < 7; i++) {
      const d = new Date(`${weekStart}T00:00:00`);
      d.setDate(d.getDate() + i);
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      slots.push({
        iso,
        weekday: d.toLocaleDateString(undefined, { weekday: "short" }),
        dayNum: d.getDate(),
      });
    }
  }

  const isDateComplete = (iso) => {
    const segs = daysByDate.get(iso);
    if (!segs || segs.length === 0) return false;
    return segs.every((s) => completedIds.has(s.id));
  };

  return (
    <div className="flex flex-col gap-space-md">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col">
          <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
            This Week
          </span>
          <p className="text-body-neu-md text-slate-muted">
            {weekStart && weekEnd
              ? `${weekStart} → ${weekEnd}`
              : "Loading…"}
          </p>
        </div>
        {showCloseButton && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close weekly plan"
            className={`w-10 h-10 rounded-full flex items-center justify-center bg-canvas-base text-neu-primary ${PAGE_RAISED_SM} hover:shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_5px_var(--outer-shadow-dark),inset_-2px_-2px_5px_var(--outer-shadow-light)] transition-all`}
          >
            <FiX />
          </button>
        )}
      </div>

      {loading && <p className="text-body-md text-slate-muted">Loading week…</p>}
      {error && !loading && <p className="text-body-md text-[#b02a37]">{error}</p>}

      {!loading && !error && (
        <ul className="flex flex-col gap-1">
          {slots.map((slot) => {
            const segs = daysByDate.get(slot.iso) || [];
            const hasReading = segs.length > 0;
            const complete = isDateComplete(slot.iso);
            const isToday = slot.iso === todayISO;
            const isSelected = slot.iso === selectedISO;
            const references = segs
              .map((s) => s.reference_en || s.reference_ko)
              .join(" · ");

            const rowClass = [
              "flex items-center justify-between gap-2 px-3 py-2 rounded-xl transition-all",
              isSelected
                ? `bg-canvas-base ${NEU_INSET_SM}`
                : hasReading
                  ? "bg-canvas-base hover:bg-canvas-light"
                  : "opacity-40 cursor-default",
            ].join(" ");

            const badgeClass = [
              "w-9 h-9 flex-shrink-0 rounded-full flex items-center justify-center text-label-sm font-bold",
              complete
                ? "bg-accent-cta text-white"
                : isToday
                  ? "bg-canvas-base text-accent-cta border-2 border-accent-cta"
                  : `bg-canvas-base text-slate-muted ${NEU_INSET_SM}`,
            ].join(" ");

            const rowContent = (
              <>
                <div className="flex items-center gap-space-sm min-w-0">
                  <div className={badgeClass} aria-hidden="true">
                    {complete ? <FiCheck /> : slot.dayNum}
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className={`text-label-sm ${isToday ? "text-accent-cta font-bold" : "text-slate-muted"}`}>
                      {slot.weekday}
                    </span>
                    <span className="text-label-md text-neu-primary font-semibold truncate">
                      {references || "—"}
                    </span>
                  </div>
                </div>
              </>
            );

            return (
              <li key={slot.iso}>
                {hasReading ? (
                  <button
                    type="button"
                    onClick={() => onSelect && onSelect(slot.iso)}
                    className={`w-full text-left ${rowClass}`}
                    aria-current={isSelected ? "page" : undefined}
                    aria-label={`${slot.weekday} ${slot.iso}${complete ? ", completed" : ""}${isToday ? ", today" : ""}`}
                  >
                    {rowContent}
                  </button>
                ) : (
                  <div className={rowClass} aria-label={`${slot.weekday}, no scheduled reading`}>
                    {rowContent}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex flex-col gap-space-xs pt-space-xs">
        {todayISO && (
          <Link
            to={`/bible-reading/plan/${planSlug}/${todayISO}`}
            onClick={onClose}
            className={`inline-flex items-center justify-center gap-2 h-10 rounded-full bg-accent-cta text-white text-label-md font-semibold shadow-[-3px_-3px_7px_var(--outer-shadow-light),4px_5px_10px_rgba(0,0,0,0.22)] hover:bg-accent-cta-hover active:shadow-[inset_2px_2px_5px_rgba(0,0,0,0.3),inset_-2px_-2px_5px_rgba(255,255,255,0.2)] transition-all`}
          >
            Return to Today
          </Link>
        )}
        <Link
          to={`/reading-plans/${planSlug}`}
          onClick={onClose}
          className={`inline-flex items-center justify-center gap-1 h-10 rounded-full bg-canvas-base text-neu-primary text-label-md font-semibold ${PAGE_RAISED_SM} hover:shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_5px_var(--outer-shadow-dark),inset_-2px_-2px_5px_var(--outer-shadow-light)] transition-all`}
        >
          <FiCalendar />
          <span>Full Plan</span>
        </Link>
        {planName && (
          <p className="text-label-sm text-slate-muted text-center mt-1">{planName}</p>
        )}
      </div>
    </div>
  );
};

export default ReadingPlanWeek;
